import React, { useCallback } from 'react';
import { Grid } from '~/components/';
import { useForm } from '~/hooks';
import { MdAddCircle } from 'react-icons/md';
import { FaPencilAlt, FaTrash} from 'react-icons/fa';
import AddInsured from './AddInsured';
import { useSectionLoader, useSaveSectionDetail, useDeleteSectionDetail } from '../../hooks';
import sectionTypes from '../sectionTypes';


/**
 * @typedef InsuredValues
 * @property {number}   sectionId
 * @property {string}   titleOpinionId 
 * @property {object}   insuredType 
 * @property {object}   insuredPrimary 
 * @property {object}   insuredSecondary 
 * @property {[object]} insuredTypeOptions 
 * @property {[object]} sectionDetails
 * @property {object}   relationship
 * @property {[object]} relationshipOptions
 * @property {boolean}  addInsuredFlag
 * 
 * 
 * @typedef FormState
 * @property {InsuredValues} values The current form values
 */

/** @type {InsuredValues} */

const defaultState = {
    id: 0,
    sectionId: 0,
    insuredType: { label: '...', value: '' },
    insuredTypeOptions: [],
    relationship: { label: '...', value: '' },
    relationshipOptions: [],
    sectionDetails: [],
    data: [],
    addInsuredModalFLag: false,
    insuredDetailData: {},
    editInsured: false,
    sectionType: sectionTypes.ADDITIONAL_INFO
};

const loadData = async (values) => {
    const defaultInsuredType = defaultState.insuredTypeOptions.find(s => s.id == 1);
    return {
        ...values,
        sectionId: values ? values.sectionId: 0,
        insuredPrimaryType: values? values.insuredPrimaryType : defaultInsuredType,
        insuredSecondaryType: values? values.insuredSecondaryType :  defaultInsuredType
    };
};

const Insured = (props) => {
    const { onLoad: loadSection } = useSectionLoader(sectionTypes.TITLE_INSURANCE_APPLICATION, props.titleOpinionId);

    const onLoad = useCallback((values) => {
        return loadData(values).then(loadSection(values));
    }, [props.sectionDetails]);

    const insuredDetail =  (props.sectionDetails && Object.keys(props.sectionDetails).length > 0) ? props.sectionDetails.map((detail) => {
        
        const primaryInsuredName = detail._data.insuredPrimaryType.value == 'Individual' ?
            (Object.values(detail._data.insuredPrimary).join(' '))
            : detail._data.insuredPrimary.insuredEntityName + ', ' + detail._data.insuredPrimary.insuredEntityType.label;

        const secondaryInsuredName = detail._data.insuredSecondary && Object.keys(detail._data.insuredSecondary).length > 0 ?
            detail._data.insuredSecondaryType.value == 'Individual' ?
                (Object.values(detail._data.insuredSecondary).join(' '))
                : detail._data.insuredSecondary.insuredEntityName + ', ' + detail._data.insuredSecondary.insuredEntityType.label        
            :
            null;

        const insuredName = detail._data.relationship ? (secondaryInsuredName ? primaryInsuredName + ' ' + detail._data.relationship.label + ' ' + secondaryInsuredName 
            : (primaryInsuredName + ' ' + detail._data.relationship.label)) : (primaryInsuredName);
        
        return {
            ...detail,
            id: detail.sectionDetailId,
            insuredName: insuredName            
        } ;
    }) : [];

    const { state, fieldActions } = useForm({
        initialState: {...defaultState},
        onLoad,
        debug: true 
    });    

    const { deleteSectionDetail } = useDeleteSectionDetail(state.values.data, state.values.sectionDetailId);

    const onEditClick = async (data) => {
        await fieldActions.onChange(data, 'insuredDetailData');
        await fieldActions.onChange(true, 'editInsured');
        fieldActions.onChange(true, 'addInsuredModalFLag');
    };

    const onDeleteClick = (id) => {
        deleteSectionDetail(id);
    };

    const addInsuredModal = () => {
        fieldActions.onChange(false, 'editInsured');
        fieldActions.onChange(true, 'addInsuredModalFLag');
    };

    const cancelAddInsured = () => {
        fieldActions.onChange(false, 'addInsuredModalFLag');
    }; 

    const { saveField } = useSaveSectionDetail(state.values.data, state.values.sectionType, props.initialDefaultData, props.titleOpinionId, state.values.sectionDetailId);
    
    const addInsured = async (values) => {

        const data = {
            sectionId: props.sectionId,
            type: 'insured',
            insuredPrimaryType: values.insuredPrimaryType,
            insuredPrimary: values.insuredPrimary,
            relationship: values.relationship,
            insuredSecondaryType: values.insuredSecondaryType,
            insuredSecondary: values.insuredSecondary            
        };

        await fieldActions.onChange(data, 'data');
        await saveField( state.values.data, state.values.sectionType, props.initialDefaultData, props.titleOpinionId, state.values.sectionDetailId);
        await fieldActions.onChange(false, 'addInsuredModalFLag');
        
    };

    const saveInsured = async (prevData, newData) => {
        const data = {
            sectionId: props.sectionId,
            sectionDetailId: prevData.sectionDetailId,
            type: 'insured',
            insuredPrimaryType: newData.insuredPrimaryType,
            insuredPrimary: newData.insuredPrimary ? newData.insuredPrimary : prevData._data.insuredPrimary,
            relationship: newData.relationship,
            insuredSecondaryType: newData.insuredSecondaryType,
            insuredSecondary: newData.insuredSecondary ? newData.insuredSecondary : prevData._data.insuredSecondary    
        };
        
        await fieldActions.onChange(data, 'data');
        await saveField( state.values.data, state.values.sectionType, props.initialDefaultData, props.titleOpinionId, data.sectionDetailId);
        fieldActions.onChange(false, 'addInsuredModalFLag');
    };

    const columns = [
        {
            field: 'insuredName',
            width: 500
        },
        {
            field: 'edit',
            width: 150,
            canSort: false,
            cell: ({ data }) => {
                return (
                    <>
                        {!props.editMode ? <span className="h-10" /> :
                            <>
                                <button className="btn btn-link rightSpace" 
                                    onClick={() => onEditClick(data)}>
                                    <FaPencilAlt />
                                </button>
                            </>
                        }                        
                    </>
                    
                );
            }
        },
        {
            field: 'delete',
            width: 150,
            canSort: false,
            cell: ({ data }) => {
                return (
                    <>
                        {!props.editMode ? <span className="h-10" /> :
                            <>                    
                                <button className="btn btn-link rightSpace" 
                                    onClick={() => onDeleteClick(data.id)}>
                                    <FaTrash />
                                </button>
                            </>
                        }                        
                    </>
                    
                );
            }
        }
    ];

    return (
        <React.Fragment>
            
            <div className="col-span-12">
                <Grid
                    className="grid-stretch"
                    columns={columns}
                    data={insuredDetail}
                />

                <AddInsured
                    isModalOpen={state.values.addInsuredModalFLag}
                    insuredDetailData={state.values.insuredDetailData}
                    editInsured={state.values.editInsured}
                    onCancel={() => cancelAddInsured()}
                    headerTxt={'Add Insured'}
                    sectionId={props.sectionId}
                    addInsured={(values) => addInsured(values)}
                    saveInsured={(a,b) => saveInsured(a,b)}
                />
            </div>
            {!props.editMode ?  <span className='col-span-12 mb-8' /> : 
                <div className='mt-auto col-span-12'>
                    <div className="flex justify-start items-center mt-auto">
                        <button
                            className='py-2 px-2 rounded-full text-white flex items-center focus:outline-none bg-blue-60 disabled:opacity-50'
                            onClick={() => addInsuredModal()}
                        >
                            <MdAddCircle className="mr-2" size="1.25rem" />
                            Add Insured
                        </button>
                    </div>
                </div>
            }
        </React.Fragment>
    );
};

export default Insured;